import type {
  DeprecatedTabsContentOption,
  DeprecatedTabsTriggerOption,
} from "@v1/components/designSystem/tabs/Tabs";
import type { ProductRef } from "schemas/product";

import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { DeprecatedTabs } from "@v1/components/designSystem/tabs/Tabs";
import ElementNameEditor from "@v1/components/editor/elementEditors/ElementNameEditor";
import { ShopifyThemeSettings } from "@v1/components/editor/elementEditors/ShopifyThemeSettings";
import { ProductSelectionCombobox } from "@v1/components/editor/page/ProductSelectionCombobox";
import { useElementEditorDataContext } from "@v1/contexts/ElementEditorDataContext";
import { useInitial } from "@v1/hooks/useInitial";

import ElementCustomCssEditor from "./ElementCustomCssEditor";
import { ElementEditorHeading } from "./ElementEditorHeading";

type ProductTemplateElementEditorTab = "general" | "shopifyTheme" | "advanced";

const PRODUCT_TEMPLATE_EDITOR_TABS: DeprecatedTabsTriggerOption[] = [
  { value: "general", label: "General" },
  { value: "shopifyTheme", label: "Shopify Theme" },
  { value: "advanced", label: "Advanced" },
];

const ProductTemplateElementEditor: React.FC<{
  initialName?: string;
}> = ({ initialName }) => {
  const [selectedTab, setSelectedTab] =
    React.useState<ProductTemplateElementEditorTab>("general");

  const PRODUCT_TEMPLATE_EDITOR_CONTENT: DeprecatedTabsContentOption[] = [
    {
      value: "general",
      tabContent: <ProductTemplateBasicSettings initialName={initialName} />,
    },
    { value: "shopifyTheme", tabContent: <ShopifyThemeSettings /> },
    {
      value: "advanced",
      tabContent: <ElementCustomCssEditor />,
    },
  ];

  return (
    <div className="flex h-fit flex-col">
      <DeprecatedTabs
        tabsOptions={PRODUCT_TEMPLATE_EDITOR_TABS}
        contentOptions={PRODUCT_TEMPLATE_EDITOR_CONTENT}
        value={selectedTab}
        onValueChange={(value) => {
          setSelectedTab(value as ProductTemplateElementEditorTab);
        }}
        size="sm"
        defaultValue={selectedTab}
      />
    </div>
  );
};

const ProductTemplateBasicSettings: React.FC<{
  initialName?: string;
}> = ({ initialName }) => {
  const { element, onChangeElement } = useElementEditorDataContext();
  const initialElementIsPublished = useInitial(element.isPublished);

  const handleProductSelectionChange = (newValue: ProductRef[] | null) => {
    const newTemplateShopifyProductIds = (newValue ?? []).map((ref) => {
      return String(ref.productId);
    });

    onChangeElement({
      ...element,
      templateShopifyProductIds: newTemplateShopifyProductIds,
    });
  };

  return (
    <div className="flex h-fit flex-col gap-y-4">
      <ElementNameEditor
        type="shopifyProductTemplate"
        inputName="page-name"
        initialName={initialName ?? element.name}
        onChange={(name) => onChangeElement({ ...element, name })}
      />
      <DeprecatedLabeledControl label="Path" size="sm">
        <div className="flex flex-row items-center gap-2">
          <div className="flex-none">
            <DeprecatedInput
              value="products/"
              size="sm"
              layoutClassName="w-20"
              isDisabled={true}
            />
          </div>
          <DeprecatedInput
            id="page-data-path"
            placeholder="product-slug"
            value="product-slug"
            size="sm"
            isDisabled
          />
        </div>
      </DeprecatedLabeledControl>

      {initialElementIsPublished && (
        <SwitchWithDescription
          label="Is Published"
          description="Your products will be displayed using this template."
          isOn={element.isPublished}
          onChange={(isPublished) => {
            return onChangeElement({ ...element, isPublished });
          }}
          size="sm"
          variant="card"
        />
      )}

      <Separator />

      <ProductSelector
        selectedIds={element.templateShopifyProductIds ?? []}
        onChange={handleProductSelectionChange}
      />
    </div>
  );
};

const ProductSelector: React.FC<{
  selectedIds: string[];
  onChange: (newValue: ProductRef[] | null) => void;
}> = ({ selectedIds, onChange }) => {
  const selectedProductRefs = React.useMemo(() => {
    return selectedIds.map((id) => ({ productId: Number(id) }));
  }, [selectedIds]);

  return (
    <div className="mt-2">
      <ElementEditorHeading>
        Products Assigned to Template
        {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
      </ElementEditorHeading>
      <div className="my-2 min-h-[32px]">
        <ProductSelectionCombobox
          value={selectedProductRefs}
          onChange={onChange}
          isMultiselect
          side="right"
          sideOffset={20}
        />
      </div>
    </div>
  );
};

export default ProductTemplateElementEditor;
